/**
 * The connector library's column filters (item 83, Segev 2026-11-02): every
 * column of the list narrows it from its own heading — a click on the
 * heading opens its choices, each with how many rows have it. What is in
 * force shows as removable chips above the list, with "Clear all". On a
 * phone, where most columns fold away, one "Filters" sheet holds them all.
 * The rules are utils/libraryList.ts (filterRows, columnChoices).
 */

import { useState, type ReactNode } from 'react';
import { Checkbox, Drawer, Popover, Radio } from 'antd';
import { useLanguage } from '@hooks/useLanguage';
import { mirrorPlacement } from '@/utils/rtl';

/** One column's filter: its heading, its choices, whether it narrows the list. */
export interface ColumnFilter {
  key: string;
  /** The column's heading (translated). */
  heading: string;
  active: boolean;
  panel: ReactNode;
}

/** A heading that opens its column's choices. */
export function FilterHeading({ filter, children }: { filter: ColumnFilter; children?: ReactNode }) {
  const { t, direction } = useLanguage();
  const [open, setOpen] = useState(false);
  return (
    <Popover content={<div className="cxl-colf" data-testid={`connector-library-colfilter-${filter.key}-panel`}>{filter.panel}</div>}
             trigger={['click']} open={open} onOpenChange={setOpen} arrow={false}
             placement={mirrorPlacement(direction, 'bottomLeft')} overlayClassName="cxl-orgfilter-pop" destroyOnHidden>
      <button type="button" className={`cxl-sort cxl-colh ${filter.active ? 'is-on' : ''}`} aria-haspopup="dialog"
              aria-expanded={open} title={t('connectorLibrary.colFilter.open', { column: filter.heading })}
              data-testid={`connector-library-colfilter-${filter.key}`}>
        {children ?? filter.heading}
        <svg className="cxl-colh__glyph" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
      </button>
    </Popover>
  );
}

/** Boxes, one per value in view, with its count; none ticked keeps everything. */
export function Choices<V extends string>({ title, choices, selected, onChange, searchable }: {
  title: string;
  /** `view`: how a choice is drawn (names isolated); `text`: what the search reads. */
  choices: { value: V; view: ReactNode; text: string; count: number }[];
  selected: Set<V>;
  onChange: (next: Set<V>) => void;
  /** A long list (organizations) gets a search. */
  searchable?: boolean;
}) {
  const { t } = useLanguage();
  const [query, setQuery] = useState('');
  const q = query.trim().toLocaleLowerCase();
  const shown = q ? choices.filter(c => c.text.toLocaleLowerCase().includes(q)) : choices;
  const flip = (value: V) => {
    const next = new Set(selected);
    if (next.has(value)) next.delete(value); else next.add(value);
    onChange(next);
  };
  return (
    <fieldset className="cxl-colf__group">
      <legend className="cxl-colf__title">{title}</legend>
      {searchable && choices.length > 6 && (
        <input className="form-input cxl-colf__search" type="search" value={query} dir="auto"
               onChange={e => setQuery(e.target.value)} placeholder={t('connectorLibrary.orgFilter.search')}
               aria-label={t('connectorLibrary.orgFilter.search')} />
      )}
      <div className="cxl-colf__list">
        {shown.map(c => (
          <Checkbox key={c.value} checked={selected.has(c.value)} onChange={() => flip(c.value)} className="cxl-colf__choice"
                    data-testid={`connector-library-colfilter-choice-${c.value}`}>
            <span className="cxl-colf__label">{c.view}</span>
            <span className="cxl-colf__count"><bdi dir="ltr">{c.count}</bdi></span>
          </Checkbox>
        ))}
        {shown.length === 0 && <p className="cxl-panel__empty">{t('connectorLibrary.orgFilter.noMatch')}</p>}
      </div>
      {selected.size > 0 && (
        <button type="button" className="cxl-row__link cxl-colf__clear" onClick={() => onChange(new Set())}>
          {t('connectorLibrary.colFilter.clear')}
        </button>
      )}
    </fieldset>
  );
}

/** One of a few (in use / not used). */
export function OneOf<V extends string>({ title, value, options, onChange }: {
  title: string; value: V; options: { value: V; label: string }[]; onChange: (v: V) => void;
}) {
  return (
    <fieldset className="cxl-colf__group">
      <legend className="cxl-colf__title">{title}</legend>
      <Radio.Group className="cxl-colf__list" value={value} onChange={e => onChange(e.target.value as V)}>
        {options.map(o => (
          <Radio key={o.value} value={o.value} className="cxl-colf__choice"
                 data-testid={`connector-library-colfilter-choice-${o.value}`}>{o.label}</Radio>
        ))}
      </Radio.Group>
    </fieldset>
  );
}

/** What narrows the list now: a chip per filter, each removable, and "Clear all". */
export function FilterChips({ chips, onClearAll }: {
  chips: { key: string; text: ReactNode; label: string; onRemove: () => void }[];
  onClearAll: () => void;
}) {
  const { t } = useLanguage();
  if (chips.length === 0) return null;
  return (
    <div className="cxl-chips" data-testid="connector-library-chips">
      {chips.map(c => (
        <span key={c.key} className="cxl-chip" data-testid={`connector-library-chip-${c.key}`}>
          <span className="cxl-chip__text">{c.text}</span>
          <button type="button" className="cxl-chip__remove" onClick={c.onRemove}
                  aria-label={t('connectorLibrary.colFilter.remove', { filter: c.label })}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"
                 strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </span>
      ))}
      {chips.length > 1 && (
        <button type="button" className="cxl-row__link" onClick={onClearAll} data-testid="connector-library-chips-clear">
          {t('connectorLibrary.colFilter.clearAll')}
        </button>
      )}
    </div>
  );
}

/** A phone: every column's choices in one sheet. */
export function FiltersSheet({ filters, count }: { filters: ColumnFilter[]; count: number }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={`cxl-orgfilter__button ${count > 0 ? 'is-on' : ''}`} onClick={() => setOpen(true)}
              aria-haspopup="dialog" aria-expanded={open} data-testid="connector-library-filters">
        <span className="cxl-orgfilter__value">
          {count > 0 ? t('connectorLibrary.colFilter.sheetCount', { count }) : t('connectorLibrary.colFilter.sheet')}
        </span>
      </button>
      <Drawer open={open} placement="bottom" onClose={() => setOpen(false)} title={t('connectorLibrary.colFilter.sheet')}
              height="85vh" className="cxl-scopef-sheet" destroyOnHidden>
        <div className="cxl-colf cxl-colf--sheet">
          {filters.map(f => <div key={f.key} className="cxl-colf__section">{f.panel}</div>)}
          <div className="cxl-scopef__foot">
            <button type="button" className="cx-btn cx-btn--primary" onClick={() => setOpen(false)}>
              {t('connectorLibrary.scope.done')}
            </button>
          </div>
        </div>
      </Drawer>
    </>
  );
}
